import { useEffect, useState } from 'react';
import type { Phase, GameId } from '@ringmaster/shared';

/**
 * GameHeader — the shared, minimal top header for BOTH the Host post-start screens
 * and the Hand (player) screens, so host and players see the identical phase label
 * + timer.
 *
 * Simplified anatomy (per the mockup — NO wordmark/banner art):
 *  - plain #080808 background
 *  - phase label in grey Inter ~15 ("Day 1 - Preparation")
 *  - big white Inter bold ~44 timer "00:29" (hidden when no active deadline)
 *  - thin gold divider (~64px wide, centered)
 */

export interface GameHeaderProps {
  phase: Phase;
  round: number;
  /** Epoch-ms deadline for the current phase, or null when untimed. */
  timerEndsAt: number | null;
  /** True while the host has the game paused (countdown freezes, not resets). */
  paused?: boolean;
  /** Frozen ms remaining while paused (server-provided). */
  timerRemainingMs?: number | null;
  /** Which game — relabels the shared LOBBY/DEAL beats per game. */
  gameId?: GameId;
}

/** Human phase label per the design spec's phase mapping. */
export function phaseLabel(phase: Phase, round: number, gameId?: GameId): string {
  // The Whisper shares the LOBBY/DEAL phases with Blood Moon but reads as a séance,
  // not a village day — relabel the shared opening beat.
  if (gameId === 'whisper' && phase === 'DEAL') return 'The séance opens';
  // One Night has exactly ONE night and no day count, so "Day 1 - " is a lie the
  // rest of its labels don't tell (Nightfall / Dawn / Discussion / The Vote all
  // run bare). Same shared DEAL phase, different game — relabel it here.
  if (gameId === 'onenight' && phase === 'DEAL') return 'Preparation';
  switch (phase) {
    case 'LOBBY':
      return 'Lobby';
    case 'DEAL':
      return 'Day 1 - Preparation';
    case 'NIGHT_INTRO':
    case 'NIGHT_WOLVES':
    case 'NIGHT_INTERLUDE':
    case 'NIGHT_SEER':
    case 'RESOLVE_NIGHT':
      return `Night ${round}`;
    case 'DAWN':
      return `Day ${round} - Dawn`;
    case 'DISCUSS':
      return `Day ${round} - Discussion`;
    case 'VOTE':
      return `Day ${round} - Vote`;
    case 'RESOLVE_VOTE':
      return `Day ${round} - Judgement`;
    // ── The Whisper — placeholder labels (final copy in M6). TODO(whisper M6).
    case 'WHISPER_INTRO':
      return `Round ${round}`;
    case 'QUESTION':
      return `Round ${round} - The Whisper`;
    case 'RESOLVE_GUESS':
      return `Round ${round}`;
    case 'HUNT':
      return `Round ${round} - The Hunt`;
    case 'RESOLVE_HUNT':
      return `Round ${round} - Reveal`;
    case 'SCORE':
      return `Round ${round} - Score`;
    // ── Blood Moon: One Night — a single night, no day-count (placeholder
    // labels; final copy TBD). TODO(onenight): dedicated copy pass.
    case 'ONENIGHT_NIGHTFALL':
      return 'Nightfall';
    case 'ONENIGHT_NIGHT_ACTIONS':
      return 'The Night Stirs';
    case 'ONENIGHT_DAWN':
      return 'Dawn';
    case 'ONENIGHT_DISCUSS':
      return 'Discussion';
    case 'ONENIGHT_VOTE':
      return 'The Vote';
    case 'ONENIGHT_RESOLVE':
      return 'Judgement';
    case 'END':
      return 'Game over';
    default: {
      const _exhaustive: never = phase;
      void _exhaustive;
      return '';
    }
  }
}

function fmt(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function GameHeader({
  phase,
  round,
  timerEndsAt,
  paused = false,
  timerRemainingMs = null,
  gameId,
}: GameHeaderProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (timerEndsAt == null || paused) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [timerEndsAt, paused]);

  const label = phaseLabel(phase, round, gameId);
  // While paused the server sends the frozen remaining ms — render that, static.
  const frozenLeft = paused ? timerRemainingMs : null;
  const hasTimer = frozenLeft != null || timerEndsAt != null;
  const left = frozenLeft ?? (timerEndsAt != null ? timerEndsAt - now : 0);

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        background: 'var(--bg-base)',
        paddingTop: 'max(var(--space-5), env(safe-area-inset-top, 0px) + var(--space-4))',
        paddingBottom: 'var(--space-4)',
      }}
    >
      {/* Phase label — Grenze display serif for a playbill feel; scale bumped a
          touch so the serif stays legible on the host overlay. (The old IM Fell
          face was inherently small-caps and rendered this as caps for free;
          Grenze is not, so this label now reads in its written case.) */}
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 17,
          color: 'var(--text-secondary)',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </div>

      {/* Big white timer — Inter bold ~44 (hidden when no active deadline) */}
      {hasTimer && (
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 44,
            fontWeight: 700,
            lineHeight: 1,
            color: paused
              ? 'var(--text-tertiary)'
              : left > 0 && left <= 10_000
                ? 'var(--accent-blood-end)'
                : 'var(--text-primary)',
            fontVariantNumeric: 'tabular-nums',
            transition: 'color var(--duration-base) var(--ease-standard)',
          }}
        >
          {fmt(left)}
        </div>
      )}

      {/* Pause badge — every device sees the game is held */}
      {paused && (
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#f2d9a0',
            border: '1px solid rgba(201,164,78,0.45)',
            borderRadius: 'var(--radius-pill)',
            padding: '3px 12px',
          }}
        >
          Paused
        </div>
      )}

      {/* Thin gold divider (~64px, centered) — retinted to the --gold-3 family. */}
      <div
        aria-hidden
        style={{
          marginTop: hasTimer ? 6 : 2,
          width: 64,
          height: 1,
          background:
            'linear-gradient(90deg, rgba(169,118,47,0) 0%, var(--gold-3, #a9762f) 50%, rgba(169,118,47,0) 100%)',
        }}
      />
    </div>
  );
}
